import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps } from '@chakra-ui/react';
import { MarkdownLink } from '@src/components/sco/MarkdownRenderer/MarkdownLink';
import { MermaidDiagram } from '@src/components/sco/MarkdownRenderer/MermaidDiagram';
import { RyansTakeBlockquote } from '@src/components/sco/MarkdownRenderer/RyansTakeBlockquote';
import { ScoCitation } from '@src/components/sco/MarkdownRenderer/ScoCitation';
import { ScoOption, ScoQuestion } from '@src/components/sco/MarkdownRenderer/ScoQuestion';
import { presets, typographyStyles } from '@src/components/sco/MarkdownRenderer/typographyStyles';
import { isValidElement, ReactNode, useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

// Preserve mention:user:… deep links used in chat, while restoring react-markdown's
// default scheme stripping for everything else (blocks javascript:, data:, etc.).
const chatUrlTransform = (uri: string) => uri.startsWith('mention:') ? uri : defaultUrlTransform(uri);

// Recursively pull the plain text out of a React node tree (strings, arrays,
// and elements with nested `children` props).
const extractText = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  if (isValidElement(node)) {
    return extractText((node.props as {
      children?: ReactNode;
    }).children);
  }
  return '';
};

// Match "Ryan's Take" case-insensitively, tolerating straight or curly apostrophes.
const containsRyansTake = (node: ReactNode): boolean => /ryan[’']s\s+take/i.test(extractText(node));

// Strip the extra outer brackets from wiki-style links so they render as a normal link, e.g.
// `[[Foundation Model](/lexicon/foundation-model)]` -> `[Foundation Model](/lexicon/foundation-model)`
const stripWikiLinkBrackets = (value?: string): string | undefined => value?.replace(/\[(\[[^\]]*]\([^)]*\))]/g, '$1');

// Content of a blockquote line, with the leading `>` marker and one optional space removed.
const blockquoteContent = (line: string): string => line.replace(/^\s*>\s?/, '');
const isBlockquoteLine = (line: string): boolean => /^\s*>/.test(line);

// With `remarkBreaks` active, every single newline becomes a hard <br>, so a blockquote
// written as one line per sentence renders with a forced break after each. This collapses
// those single (soft) breaks within a blockquote into spaces so the quote flows as a
// paragraph, while preserving blank `>` lines as real paragraph breaks. e.g.
//   > Line one
//   > Line two
// becomes
//   > Line one Line two
const stripBlockquoteSingleBreaks = (value?: string): string | undefined => {
  if (!value) {
    return value;
  }
  const result: string[] = [];
  for (const line of value.split('\n')) {
    const prev = result[result.length - 1];
    const canMerge = prev !== undefined && isBlockquoteLine(line) && isBlockquoteLine(prev) && blockquoteContent(line).trim() !== '' && blockquoteContent(prev).trim() !== '';
    if (canMerge) {
      result[result.length - 1] = `${prev.replace(/\s+$/, '')} ${blockquoteContent(line).trim()}`;
    } else {
      result.push(line);
    }
  }
  return result.join('\n');
};
const rehypeTypingPlugin = () => (tree: any) => {
  const visit = (node: any) => {
    if (node.type === 'text' && node.value && node.value.trim().length > 0) {
      const originalValue = node.value;
      node.type = 'element';
      node.tagName = 'typing-text-node';
      node.properties = {};
      node.children = [{
        type: 'text',
        value: originalValue
      }];
      return;
    }
    if (node.children) {
      if (node.type === 'element' && (node.tagName === 'code' || node.tagName === 'pre')) {
        return;
      }
      node.children.forEach(visit);
    }
  };
  visit(tree);
};
export type ChatMessageRole = 'user' | 'assistant';
export type MarkdownRendererProps = {
  baseFontSize?: BoxProps['fontSize'];
  baseHeadingColor?: string;
  baseTextColor?: string;
  chatRole?: ChatMessageRole;
  children?: string;
  typographyStyles?: Record<string, any>;
  preset?: string;
  typingEffect?: boolean;
  delayedText?: boolean;
  injectParagraphsIfBreaksNotPresent?: boolean;
  boxProps?: BoxProps;
  stripWikiLinkBrackets?: boolean;
  stripBlockquoteSingleBreaks?: boolean;
};
export const MarkdownRenderer = props => {
  const $ = _c(28);
  let T0;
  let markdownContent;
  let t0;
  let t1;
  let t2;
  if ($[0] !== props) {
    const preset = presets[props.preset] || {};
    const rolePreset = props.chatRole ? preset.roles?.[props.chatRole] : undefined;
    const baseTextColor = props.baseTextColor ?? rolePreset?.baseTextColor ?? "fg";
    let value = props.children;
    if (props.injectParagraphsIfBreaksNotPresent && props.children?.indexOf("<br>") === -1 && props.children?.indexOf("\n") !== -1) {
      let t3;
      if ($[6] !== props.children) {
        t3 = props.children?.replaceAll("\n\n", "\n\n<br>");
        $[6] = props.children;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      value = t3;
    }
    if (props.stripWikiLinkBrackets) {
      let t3;
      if ($[8] !== value) {
        t3 = stripWikiLinkBrackets(value);
        $[8] = value;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      value = t3;
    }
    if (props.stripBlockquoteSingleBreaks) {
      let t3;
      if ($[10] !== value) {
        t3 = stripBlockquoteSingleBreaks(value);
        $[10] = value;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      value = t3;
    }
    markdownContent = value;
    T0 = Box;
    t1 = "longform";
    t2 = props.baseFontSize;
    t0 = typographyStyles({
      ...(preset.typographyStyles || {}),
      ...(rolePreset?.typographyStyles || {}),
      ...(props.typographyStyles || {})
    }, {
      baseTextColor
    });
    $[0] = props;
    $[1] = T0;
    $[2] = markdownContent;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    T0 = $[1];
    markdownContent = $[2];
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[12] !== t0) {
    t3 = {
      ...t0
    };
    $[12] = t0;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== props.typingEffect) {
    t4 = props.typingEffect ? [rehypeRaw, rehypeTypingPlugin] : [rehypeRaw];
    $[14] = props.typingEffect;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [remarkGfm, remarkBreaks];
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      a: MarkdownLink,
      code: _temp,
      blockquote: _temp2,
      "sco-citation": ScoCitation,
      "sco-question": ScoQuestion,
      "sco-option": ScoOption
    };
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== markdownContent || $[19] !== t4) {
    t7 = <ReactMarkdown rehypePlugins={t4} remarkPlugins={t5} urlTransform={chatUrlTransform} components={t6}>{markdownContent}</ReactMarkdown>;
    $[18] = markdownContent;
    $[19] = t4;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== T0 || $[22] !== props.boxProps || $[23] !== t1 || $[24] !== t2 || $[25] !== t3 || $[26] !== t7) {
    t8 = <T0 fontFamily={t1} fontSize={t2} css={t3} {...props.boxProps}>{t7}</T0>;
    $[21] = T0;
    $[22] = props.boxProps;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
};
function _temp(t0) {
  const {
    className,
    children
  } = t0;
  if (className === "language-mermaid") {
    return <MermaidDiagram code={String(children).trimEnd()} />;
  }
  return <code className={className}>{children}</code>;
}
function _temp2(blockquote) {
  if (containsRyansTake(blockquote.children)) {
    return <RyansTakeBlockquote>{blockquote.children}</RyansTakeBlockquote>;
  }
  return <blockquote className={blockquote.className}>{blockquote.children}</blockquote>;
}